import React from "react";
import { Platform, Pressable, Text, View } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import DLOTooltip from "../../../modules/dlo/components/DLOTooltip";
import { MergedTicketsTooltip } from "./MergedTicketsTooltip";

interface MergedTicketsBadgeProps {
  /** Number of tickets merged into this one */
  ticketsMergedIntoCount: number;
  /** Array of ticket IDs that were absorbed into this ticket */
  absorbedTicketIds?: string[];
  /** Details of merged tickets (ticket number + title) */
  mergedTicketsDetails?: Array<{ ticketNumber: string; title: string }>;
  /** The ticket ID to fetch merged ticket details for */
  ticketId: string;
  /** Whether user is in read-only mode */
  isReadOnly?: boolean;
  /** Callback when badge is clicked (can be used for navigation) */
  onPress?: (ticketId: string) => void;
}

export const MergedTicketsBadge: React.FC<MergedTicketsBadgeProps> = ({
  ticketsMergedIntoCount,
  absorbedTicketIds = [],
  mergedTicketsDetails = [],
  ticketId,
  isReadOnly = false,
  onPress,
}) => {
  // Only show badge if there are merged tickets
  if (ticketsMergedIntoCount === 0 || absorbedTicketIds.length === 0) {
    return null;
  }

  const badgeContainerStyle: any = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "row",
    gap: 4,
  };

  const mergedBadgeStyle: any = {
    width: 20,
    height: 20,
    borderRadius: 10,
    backgroundColor: "#7871E2", // Purple to distinguish from duplicate (amber)
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1,
    borderWidth: 1,
    borderColor: "#7871E2",
    opacity: isReadOnly ? 0.6 : 1,
    ...(Platform.OS === "web" ? { cursor: isReadOnly ? "default" : "pointer" } as any : {}),
  };

  const badgeText: any = {
    color: "#FFFFFF",
    fontWeight: "900",
    fontSize: 12,
    textAlign: "center",
    lineHeight: 14,
  };

  const badgeContent = (
    <View style={badgeContainerStyle}>
      <Pressable
        onPress={(e) => {
          (e as any).stopPropagation?.();
          if (!isReadOnly && onPress) {
            onPress(ticketId);
          }
        }}
        style={mergedBadgeStyle}
        hitSlop={8}
      >
        <MaterialIcons name="merge-type" size={12} color="#FFFFFF" />
      </Pressable>
    </View>
  );

  // Wrap in tooltip for web, return plain for native
  if (Platform.OS === "web") {
    return (
      <DLOTooltip
        customContent={
          <MergedTicketsTooltip
            ticketId={ticketId}
            absorbedTicketIds={absorbedTicketIds}
            mergedTicketsDetails={mergedTicketsDetails}
            mergedCount={ticketsMergedIntoCount}
          />
        }
        content=""
        position="top"
        trigger="hover"
        maxWidth={380}
        minWidth={260}
        padding={1}
      >
        <View style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          {badgeContent}
        </View>
      </DLOTooltip>
    );
  }

  // For native, just return the badge without tooltip
  return badgeContent;
};
